/**
 * TriangulationConfigFields — the editable body of a {@link TrlTriangulationConfig}.
 *
 * Game-level only (unlike stalemate/radar, there is no per-node override): the
 * rule is a whole-game format choice, and a mixed map would make the minimum
 * team size impossible to reason about.
 *
 * Purely controlled: it renders `value` and reports every edit through `onChange`.
 */

import {
  TRL_BROADCAST_WINDOW_MAX_SECONDS,
  TRL_BROADCAST_WINDOW_MIN_SECONDS,
  TRL_SIMULTANEOUS_MIN_TEAM_SIZE,
  type TrlTriangulationConfig,
} from '@swtorbit/shared-socket-events';

interface TriangulationConfigFieldsProps {
  value: TrlTriangulationConfig;
  onChange: (next: TrlTriangulationConfig) => void;
  disabled?: boolean;
  /** Teams that are too small for simultaneous broadcast, for the live warning. */
  undersizedTeamNames?: string[];
}

/** Window input → clamped whole seconds (the server re-clamps on save). */
const toWindowSeconds = (raw: string): number => {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return TRL_BROADCAST_WINDOW_MIN_SECONDS;
  return Math.min(TRL_BROADCAST_WINDOW_MAX_SECONDS, Math.max(TRL_BROADCAST_WINDOW_MIN_SECONDS, n));
};

export function TriangulationConfigFields({
  value,
  onChange,
  disabled = false,
  undersizedTeamNames = [],
}: TriangulationConfigFieldsProps) {
  const showTeamWarning = value.simultaneousBroadcast && undersizedTeamNames.length > 0;

  return (
    <div className="d-flex flex-column gap-sm">
      {/* How a triangulation planet is completed. */}
      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Triangulation</span>

        <span className="fs-xs text-slate">
          A triangulation planet holds 3 screenshots. Teams always find those 3 points in any
          order — members can split up and work them at the same time.
        </span>

        <label className="d-flex align-items-center gap-2xs fs-sm">
          <input
            className="checkbox"
            type="checkbox"
            checked={value.simultaneousBroadcast}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, simultaneousBroadcast: e.target.checked })}
          />
          <span>
            <strong>Require simultaneous broadcast</strong>
            <span className="fs-xs text-slate d-block">
              When on, finding the 3 points is only half the job: the team must then stand on all
              3 points and scan together to unlock the final dig coordinates. When off, the
              coordinates are revealed the moment the 3rd point is found.
            </span>
          </span>
        </label>

        <label className="d-flex align-items-center gap-2xs fs-sm">
          All 3 scans must land within
          <input
            type="number"
            className="input"
            style={{ width: '6rem' }}
            min={TRL_BROADCAST_WINDOW_MIN_SECONDS}
            max={TRL_BROADCAST_WINDOW_MAX_SECONDS}
            step="1"
            value={value.broadcastWindowSeconds}
            disabled={disabled || !value.simultaneousBroadcast}
            onChange={(e) => onChange({ ...value, broadcastWindowSeconds: toWindowSeconds(e.target.value) })}
          />
          seconds of each other
        </label>
        <span className="fs-xs text-slate">
          The seismic tool&apos;s scan runs about 16 seconds, so 15 suits a team that starts its
          casts together. Raise it to be more forgiving. A partial attempt simply expires — teams
          can retry as often as they like.
        </span>
      </div>

      {/* The team-size rule this mode implies. */}
      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Team size</span>
        <span className="fs-xs text-slate">
          Simultaneous broadcast needs one player per point, so every team needs at least{' '}
          {TRL_SIMULTANEOUS_MIN_TEAM_SIZE} players. The same player cannot cover two points. With
          the setting off there is no minimum.
        </span>
        {showTeamWarning && (
          <span className="fs-xs text-red">
            Too few players to start: {undersizedTeamNames.join(', ')}. Add members, or turn
            simultaneous broadcast off.
          </span>
        )}
      </div>
    </div>
  );
}
